// Which fields travel between a catalogue product (ProductTemplate) and a copy
// placed from it (ProductInstance), under which name on each side, for each
// PURPOSE: placing a copy, refreshing it from the catalogue (Update drawing), a
// per-part edit (Edit properties… on a copy), and reading a copy back as a
// product (that edit's form, Save as product…). One table over every product
// field (`PRODUCT`): a new field does not compile until it says how it travels.
// The placing tools keep their geometry (position, polygon, nodes, repeat
// copies, wall seating); ids come from the caller's `gen`. Pure and DOM-free
// (test/catalogueCopy.test.mjs; docs/claude/products.md, *Placed copies*).

import type {
  Lang,
  Preparation,
  ProductCategory,
  ProductInstance,
  ProductPart,
  ProductTemplate,
} from '../shared/types';
import { cloneSkirting } from '../shared/benchPresets';
import { traitsOf } from '../shared/categories';
import { shownName } from '../shared/names';
import { DUCT_FAN_PRODUCTS } from '../shared/ductFans';
import { cloneModel3d } from '../shared/partSolids';
import { benchDepthFromSlats } from './benches';
import { catalogBase } from './heights';
import {
  copyVariant,
  firstVariant,
  refreshVariant,
} from './variants';

type InstKey = keyof ProductInstance;
type PartKey = keyof ProductPart;

/** A field no copy carries. 'catalogue': read off the product where it is used
 *  (its description, its drawing's source file, the Lighting tools' figures);
 *  'tool': which tool places it, and how; 'parts': the parts and their
 *  connections, a copy per part (multipart.ts); 'identity': the copy's link to
 *  it (`templateId`) and the built-in flag. */
interface Stays {
  stays: 'catalogue' | 'identity' | 'parts' | 'tool';
  /** A part's field for it, when a single body hands it to its first part. */
  part?: PartKey;
  /** Handed to the first part when a single body gets parts (`addPart`). */
  body?: true;
}

/** What a refresh does with a field: 'always' writes it, clearing it where the
 *  product has none; 'when-set' only where the product has one; 'edit' only on
 *  a per-part edit (Update drawing leaves it, the copy's own); 'size' the same,
 *  where the copy's size is its own (not a room's outline nor a run's nodes);
 *  'base' is Height from ground (`followCatalogBase`); 'variant' takes the
 *  product's variant with the copy's id (`refreshVariant`). */
type Update =
  | 'always'
  | 'base'
  | 'edit'
  | 'size'
  | 'variant'
  | 'when-set';

interface CarriedBase<K extends keyof ProductTemplate> {
  /** A part's field for it — a multi-part product's part carries its own —;
   *  'product': every part takes the product's; null: a part's copy has none. */
  part: PartKey | 'product' | null;
  /** Handed to the first part when a single body gets parts (`addPart`). */
  body?: true;
  /** The categories it travels for; absent, every one. */
  for?: (category: ProductCategory) => boolean;
  /** Absent: 'always'. */
  update?: Update;
  /** The copy's own value made from the product's (absent: the value itself);
   *  called with an absent one too. */
  clone?: (v: ProductTemplate[K], gen: () => string) => unknown;
  /** The product's field made from the copy's, read back (absent: the value
   *  itself): a copy keeps one of the product's variants. */
  back?: (v: unknown) => unknown;
  /** A drawing: a product that draws itself where it is placed (a room, a run)
   *  is placed without it. */
  drawing?: true;
  /** A view once read it off the product: a copy saved without it takes its
   *  product's when the drawing is opened (`adoptOnLoad`). */
  adopt?: true;
}

/** A field a copy carries, under `copy` when the copy names it otherwise. */
type Carried<K extends keyof ProductTemplate> = CarriedBase<K> & (K extends InstKey ? { copy?: InstKey } : { copy: InstKey });
type Slot<K extends keyof ProductTemplate> = Stays | Carried<K>;

/** A product keeps its height in the catalogue under its category's
 *  `heightKey` (a heater's or a shower's body as `heaterHeight`, everything
 *  else as `height`); on the copy and on a part it is `height` either way. */
const heaterBody = (c: ProductCategory): boolean => traitsOf(c).heightKey === 'heaterHeight';
const otherBody = (c: ProductCategory): boolean => traitsOf(c).heightKey === 'height';
const saunaBench = (c: ProductCategory): boolean => c === 'saunabench';

/** A room is its walls and a run its nodes: they are placed without a drawing. */
const drawsItself = (t: ProductTemplate): boolean => !!(t.autoBenches || t.polyRoom) || traitsOf(t.category).run;

const copied = <T extends object>(v: T | undefined): T | undefined => (v ? { ...v } : undefined);
const listed = <T>(v: T[] | undefined): T[] | undefined => (v ? [...v] : undefined);

const PRODUCT: { readonly [K in keyof Required<ProductTemplate>]: Slot<K> } = {
  id: { stays: 'identity' },
  builtin: { stays: 'identity' },
  name: { part: 'name' },
  nameI18n: { part: 'nameI18n', clone: copied },
  category: { part: 'product', update: 'edit' },
  width: { part: 'width', update: 'size' },
  depth: { part: 'depth', update: 'size' },
  color: { part: 'color' },
  description: { stays: 'catalogue' },
  geometry: { part: 'geometry', body: true, drawing: true, clone: (g) => (g ? { entities: g.entities.map((e) => ({ ...e })), bounds: g.bounds } : undefined) },
  dxf: { stays: 'catalogue', part: 'dxf', body: true },
  imageDataUrl: { part: 'imageDataUrl', body: true, drawing: true },
  fileName: { stays: 'catalogue', part: 'fileName', body: true },
  partName: { stays: 'catalogue' },
  lockResize: { part: 'product' },
  electrical: { part: 'product', update: 'when-set', adopt: true },
  junctionBox: { part: 'product', update: 'when-set', adopt: true },
  throughWall: { part: null },
  hotTubModel: { part: null, update: 'when-set' },
  doserModel: { part: 'doserModel', adopt: true },
  fanModel: { part: null, adopt: true },
  model3d: { part: 'model3d', body: true, adopt: true, clone: cloneModel3d },
  autoBenches: { stays: 'tool' },
  polyRoom: { stays: 'tool' },
  repeatable: { stays: 'tool' },
  offset: { stays: 'tool' },
  benchTierHeights: { part: 'benchTierHeights', body: true, update: 'when-set', clone: copied, adopt: true },
  heaterHeight: { copy: 'height', part: 'height', body: true, for: heaterBody, update: 'edit', adopt: true },
  height: { part: 'height', body: true, for: otherBody, update: 'edit', adopt: true },
  heaterGuardColor: { copy: 'heaterCoverColor', part: 'heaterCoverColor', body: true },
  heaterGap: { part: 'heaterGap', body: true, clone: copied },
  heaterCover: { part: 'heaterCover', body: true, clone: copied },
  heightFromGround: { part: 'heightFromGround', body: true, update: 'base' },
  installScrews: { stays: 'catalogue' },
  airInlet: { stays: 'catalogue' },
  powerPerMeter: { part: 'powerPerMeter', body: true },
  powerPerSqm: { part: 'powerPerSqm', body: true },
  lightKind: { stays: 'catalogue' },
  lightColour: { stays: 'catalogue' },
  lightPower: { stays: 'catalogue' },
  lightBeamAngle: { stays: 'catalogue' },
  lightLumens: { stays: 'catalogue' },
  slats: { part: null, for: saunaBench, clone: listed },
  gaps: { part: null, for: saunaBench, clone: listed },
  slatThickness: { part: null, for: saunaBench, clone: listed },
  overlap: { part: null, for: saunaBench },
  skirting: { part: null, for: saunaBench, clone: (v) => v || undefined },
  skirtingBoards: { part: null, for: saunaBench, clone: (v) => (v ? cloneSkirting(v) : undefined) },
  parts: { stays: 'parts' },
  connections: { stays: 'parts' },
  preparations: { part: 'preparations', body: true, clone: (v, gen) => (v ?? []).map((p) => ({ ...p, id: gen(), ...(p.labelI18n ? { labelI18n: { ...p.labelI18n } } : {}) })) },
  ports: { part: 'ports', body: true, clone: (v) => (v?.length ? v.map((p) => ({ ...p })) : undefined) },
  properties: { part: 'properties', clone: (v) => (v ?? []).map((p) => ({ ...p })) },
  // Read off the product by the bench carving (roomRegen.ts `heaterHoles`).
  noBenchCarve: { stays: 'catalogue' },
  // Where the drop seats it (main.ts `mountByDrop`, reading the product's or
  // its part's own `PartMount`); the copy keeps where it went.
  onRoomTop: { stays: 'tool', part: 'onRoomTop', body: true },
  underCeiling: { stays: 'tool', part: 'underCeiling', body: true },
  wallMountCentre: { stays: 'tool', part: 'wallMountCentre', body: true },
  wallMountBelowCeiling: { stays: 'tool', part: 'wallMountBelowCeiling', body: true },
  wallMountUnderside: { stays: 'tool', part: 'wallMountUnderside', body: true },
  // Set into a wall, and what it cuts there: the views read them off the copy
  // (wallCuts.ts).
  inWall: { part: 'inWall', body: true, adopt: true },
  // Back on a wall: a drag seats it again (main.ts `seatBackOnWall`).
  backOnWall: { part: 'backOnWall', body: true, adopt: true },
  wallCut: { part: 'wallCut', body: true, adopt: true, clone: copied },
  roomWallCut: { part: 'roomWallCut', body: true, adopt: true, clone: copied },
  // Its risers in a room's wall: the views and its ports read them off the copy (risers.ts).
  wallRisers: { part: 'wallRisers', body: true, adopt: true, clone: copied },
  // Rests on the benches under it and interrupts backrests: read off the copy
  // wherever it stands (main.ts `reconcileFixtures`, benchRest.ts).
  benchRest: { part: 'benchRest', body: true, adopt: true },
  cutsBackrest: { part: 'cutsBackrest', body: true, adopt: true },
  // A copy keeps the one the project uses, starting on the first; a multi-part
  // product's is its group's `source` (main.ts), so a part's copy has none.
  variants: {
    copy: 'variant', part: null, update: 'variant',
    clone: (v) => firstVariant(v),
    back: (v) => (v ? [copyVariant(v as NonNullable<ProductInstance['variant']>)] : undefined),
  },
};

type Field = keyof ProductTemplate;
type AnySlot = Stays | (CarriedBase<Field> & { copy?: InstKey });
const FIELDS = Object.keys(PRODUCT) as Field[];
const slotOf = (k: Field): AnySlot => PRODUCT[k] as AnySlot;
const isCarried = (s: AnySlot): s is CarriedBase<Field> & { copy?: InstKey } => !('stays' in s);
const copyKey = (k: Field, s: { copy?: InstKey }): InstKey => s.copy ?? (k as InstKey);
type Bag = Record<string, unknown>;

/** A part of a multi-part product, and whether it is its first (which takes the
 *  product's properties when it has none of its own). */
export interface PartOf { part: ProductPart; first: boolean }

/** What one copy stands for, under the product's names: the product, or a part
 *  of it — the part's own fields, the product's where every part takes them, its
 *  colour where it has none. */
function bodyOf(t: ProductTemplate, of?: PartOf): ProductTemplate {
  if (!of) return t;
  const { part, first } = of, src = t as unknown as Bag, own = part as unknown as Bag;
  const b: Bag = { id: t.id, builtin: t.builtin };
  for (const k of FIELDS) {
    const s = slotOf(k);
    if (!isCarried(s)) continue;
    if (s.part === 'product') b[k] = src[k];
    else if (s.part) b[k] = own[s.part];
  }
  b.color = part.color || t.color;
  b.properties = part.properties?.length ? part.properties : first ? t.properties : [];
  return b as unknown as ProductTemplate;
}

const travels = (s: CarriedBase<Field>, category: ProductCategory): boolean => !s.for || s.for(category);
const valueFor = (s: CarriedBase<Field>, v: unknown, gen: () => string): unknown =>
  s.clone ? (s.clone as (v: unknown, gen: () => string) => unknown)(v, gen) : v;

/** A sauna bench's depth is its slats' (Σ slats + Σ gaps). */
function depthFromSlats(o: Bag, category: ProductCategory): void {
  if (!saunaBench(category)) return;
  const d = benchDepthFromSlats(o as Pick<ProductInstance, 'slats' | 'gaps'>);
  if (d != null) o.depth = d;
}

export type Placed = Omit<ProductInstance, 'id' | 'x' | 'y' | 'rotation'>;

/** The catalogue's fields of a new copy of `t` (or of its part `of`): the tool
 *  placing it adds its id, its place and turn, and what it lays out itself (a
 *  room's outline and doors, a run's nodes). Its rough-ins get fresh ids from
 *  `gen`. It starts at the catalogue's Height from ground, remembered as the
 *  figure the catalogue gave it (a room's is its own). */
export function placeCopy(t: ProductTemplate, gen: () => string, of?: PartOf): Placed {
  const body = bodyOf(t, of), src = body as unknown as Bag;
  const o: Bag = { templateId: t.id };
  const room = !!(t.autoBenches || t.polyRoom);
  for (const k of FIELDS) {
    const s = slotOf(k);
    if (!isCarried(s) || !travels(s, body.category) || (s.drawing && drawsItself(t))) continue;
    if (s.update === 'base') {
      const base = catalogBase(body.heightFromGround, body.category);
      if (!room && base != null) { o.heightFromGround = base; o.catalogHeightFromGround = base; }
      continue;
    }
    const v = valueFor(s, src[k], gen);
    if (v !== undefined) o[copyKey(k, s)] = v;
  }
  depthFromSlats(o, body.category);
  if (o.junctionBox === undefined && wasJunctionBox(t)) o.junctionBox = true;
  return o as unknown as Placed;
}

/** A product or a copy saved before Junction box was a field of its own: one
 *  with the old Electrical role (`elecRole`, a Spa Manager's or a junction
 *  box's), or of the category 'spamanager', which always had one. */
export function wasJunctionBox(o: Pick<ProductTemplate, 'category'>): boolean {
  return (o as { elecRole?: unknown }).elecRole != null || o.category === 'spamanager';
}

/** Write `t` (or its part `of`) onto a copy of it. 'catalogue' (Update drawing)
 *  refreshes what the catalogue owns and leaves the copy's identity, place,
 *  size, height and drawn layout alone; Height from ground moves only while the
 *  copy still stands where the catalogue last put it. 'edit' (the per-part
 *  edit) writes everything the form shows: its category, its height and — where
 *  it is its own — its size too, and its Height from ground as typed. */
export function refreshCopy(inst: ProductInstance, t: ProductTemplate, how: 'catalogue' | 'edit', gen: () => string, of?: PartOf): void {
  const body = bodyOf(t, of), src = body as unknown as Bag, o = inst as unknown as Bag;
  const sizes: Array<[InstKey, unknown]> = [];
  for (const k of FIELDS) {
    const s = slotOf(k);
    if (!isCarried(s) || !travels(s, body.category)) continue;
    const key = copyKey(k, s), mode = s.update ?? 'always';
    if (mode === 'base') { followCatalogBase(inst, body.heightFromGround, body.category, how === 'catalogue'); continue; }
    if (mode === 'variant') { const v = refreshVariant(body.variants, inst.variant); if (v) inst.variant = v; continue; }
    if ((mode === 'edit' || mode === 'size') && how !== 'edit') continue;
    let v = valueFor(s, src[k], gen);
    if (k === 'preparations' && Array.isArray(v)) v = keepPrepIds(o[key] as Preparation[] | undefined, v as Preparation[]);
    if (mode === 'size') { sizes.push([key, v]); continue; }
    if (mode === 'always' || v != null) o[key] = v;
  }
  depthFromSlats(o, body.category);
  if (inst.polygon || inst.nodes?.length) return; // a room's or a run's size is derived
  for (const [key, v] of sizes) if (typeof v === 'number' && v > 0) o[key] = v;
}

/** The rough-ins written onto a copy keep the ids its own had — the one of the
 *  same type and label, else the next of that type in order — so what names a
 *  rough-in by its id (the schematic's client box and its wire, their edits)
 *  still names it. One the copy had none for keeps its fresh id. */
function keepPrepIds(old: readonly Preparation[] | undefined, fresh: Preparation[]): Preparation[] {
  const left = [...(old ?? [])], ids: Array<string | undefined> = fresh.map(() => undefined);
  const take = (i: number, same: (o: Preparation) => boolean): void => {
    const j = left.findIndex(same);
    if (j >= 0) ids[i] = left.splice(j, 1)[0].id;
  };
  fresh.forEach((p, i) => take(i, (o) => o.type === p.type && o.label === p.label));
  fresh.forEach((p, i) => { if (ids[i] == null) take(i, (o) => o.type === p.type); });
  return fresh.map((p, i) => (ids[i] ? { ...p, id: ids[i]! } : p));
}

/** The catalogue's Height from ground (`value`) written onto a copy. From the
 *  catalogue it moves the copy only while the copy still stands where the
 *  catalogue last put it, so a height set by hand stays; a per-part edit writes
 *  the part's own figure. A room's is its own placement. */
function followCatalogBase(inst: ProductInstance, value: number | undefined, category: ProductCategory, fromCatalog: boolean): void {
  const base = catalogBase(value, category);
  if (inst.autoBenches || base == null) return;
  if (!fromCatalog) { inst.heightFromGround = base; return; }
  const was = inst.catalogHeightFromGround;
  if (was == null || inst.heightFromGround == null || inst.heightFromGround === was) inst.heightFromGround = base;
  inst.catalogHeightFromGround = base;
}

/** A copy read back as a product: what it is now, under the product's names —
 *  the per-part edit's form (so an earlier edit of it is shown, not the
 *  catalogue's), and Save as product…. A multi-part group's parts and
 *  connections are the caller's (`partOfCopy`). */
export function productOfCopy(inst: ProductInstance): ProductTemplate {
  const o = inst as unknown as Bag;
  const b: Bag = { id: inst.templateId, builtin: false };
  for (const k of FIELDS) {
    const s = slotOf(k);
    if (!isCarried(s) || !travels(s, inst.category)) continue;
    if (s.update === 'base') { if (!inst.autoBenches && inst.heightFromGround !== undefined) b.heightFromGround = inst.heightFromGround; continue; }
    const v = o[copyKey(k, s)];
    if (v !== undefined) b[k] = s.back ? s.back(v) : v;
  }
  b.properties = inst.properties ?? [];
  return b as unknown as ProductTemplate;
}

/** A copy read back as a part of a multi-part product: everything but its id,
 *  its place in the product and its turn, which the caller lays out. */
export function partOfCopy(inst: ProductInstance): Omit<ProductPart, 'id' | 'offset'> {
  const b = productOfCopy(inst) as unknown as Bag, p: Bag = {};
  for (const k of FIELDS) {
    const s = slotOf(k);
    if (isCarried(s) && s.part && s.part !== 'product' && b[k] !== undefined) p[s.part] = b[k];
  }
  return p as unknown as Omit<ProductPart, 'id' | 'offset'>;
}

/** The fields a single body of `category` hands its first part when the
 *  product gets parts: the product's name, the part's. */
export function bodyFields(category: ProductCategory): Array<[keyof ProductTemplate, keyof ProductPart]> {
  const out: Array<[keyof ProductTemplate, keyof ProductPart]> = [];
  for (const k of FIELDS) {
    const s = slotOf(k);
    if (!s.body || !s.part || s.part === 'product' || (isCarried(s) && !travels(s, category))) continue;
    out.push([k, s.part]);
  }
  return out;
}

/** A copy from a drawing saved before it carried what the views draw it by (a
 *  fan's or a doser's model, a stored 3D model, a room's bench heights) takes
 *  its product's — a part of a multi-part product its part's (by `partId`) —,
 *  as the views once read it off the product; a built-in duct fan's off its
 *  datasheet when the catalogue does not hold it, where the plan read it. */
/** A copy saved before a name entered for it was kept apart (docs/claude/
 *  names.md): a rename wrote over its product's names. What it showed in the
 *  drawing's language, when not its product's name there, becomes its entered
 *  name, and its product's names (its part's, for a multi-part product's) are
 *  its defaults again — so nothing it shows changes. A copy whose product is
 *  gone keeps what it has. Once, when a drawing saved then is opened. */
export function settleEnteredName(inst: ProductInstance, product: ProductTemplate | null, lang: Lang): void {
  if (inst.enteredName !== undefined || !product) return;
  const part = inst.partId ? product.parts?.find((q) => q.id === inst.partId) : undefined;
  if (inst.partId && !part) return;
  const own: Pick<ProductTemplate, 'name' | 'nameI18n'> = part ?? product;
  const shown = shownName(undefined, inst.nameI18n, lang, inst.name);
  if (shown && shown !== shownName(undefined, own.nameI18n, lang, own.name)) inst.enteredName = shown;
  inst.name = own.name;
  if (own.nameI18n) inst.nameI18n = { ...own.nameI18n }; else delete inst.nameI18n;
}

export function adoptOnLoad(inst: ProductInstance, product: ProductTemplate | null): void {
  const p = product ?? DUCT_FAN_PRODUCTS.find((f) => f.id === inst.templateId);
  if (inst.junctionBox === undefined && (p?.junctionBox ?? wasJunctionBox(inst))) inst.junctionBox = true;
  delete (inst as { elecRole?: unknown }).elecRole;
  if (!p) return;
  const at = inst.partId ? (p.parts ?? []).findIndex((q) => q.id === inst.partId) : -1;
  const t = at >= 0 ? bodyOf(p, { part: p.parts![at], first: at === 0 }) : p;
  const o = inst as unknown as Bag, src = t as unknown as Bag;
  for (const k of FIELDS) {
    const s = slotOf(k);
    if (!isCarried(s) || !s.adopt || !travels(s, t.category)) continue;
    const key = copyKey(k, s);
    if (o[key] === undefined && src[k] !== undefined) o[key] = valueFor(s, src[k], () => '');
  }
}
